import { useEffect, useMemo, useState } from 'react';
import { id } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import checkinReports, {
  GetCheckinProps,
  PostCheckinFeedbacks,
  MarkAsReviewed,
} from '@/services/checkinReports';
import { FetchApiIds } from '@/types';
import { Feedback, UserCheckin, CheckinSummary } from '@/types/api';
import { formatCheckinHistory } from '@/utils/checkins';
import { getComparator } from '@/utils/order';
import CheckinDetails from './CheckinDetails.component';

const CheckinDetailsContainer = function (): JSX.Element {
  const { profile } = useProfile();
  const { userId, checkinId } = useParams();

  const getUserCheckinResponse = useApiFetch<UserCheckin, GetCheckinProps>(
    [FetchApiIds.CHECKIN_BY_ID, checkinId],
    async () => checkinReports.getCheckinById({ checkinId: checkinId as string })
  );

  const userCheckins = useApiFetch<CheckinSummary[]>(
    FetchApiIds.ALL_CHECKINS,
    checkinReports.getAllUsersCheckins,
    { enabled: !userId }
  );

  const userCheckinsByIdForAdmin = useApiFetch<CheckinSummary[]>(
    [FetchApiIds.CHECKIN_BY_ID_FOR_ADMIN, userId],
    async () => checkinReports.getUserCheckinsByIdForAdmin(userId as string),
    { enabled: !!userId }
  );

  const postCheckinFeedbacksMutation = useApiSubmit<Feedback, PostCheckinFeedbacks>(
    checkinReports.postCheckinFeedbacks
  );

  const markAsReviewedMutation = useApiSubmit<void, MarkAsReviewed>(checkinReports.markAsReviewed);

  const [isMyCheckinDetails, setIsMyCheckinDetails] = useState(false);

  useEffect(() => {
    setIsMyCheckinDetails(!!checkinId && !userId);
  }, [checkinId, userId]);

  const handlePostFeedback = async (feedback?: string): Promise<void> => {
    if (checkinId && profile.id && feedback) {
      const newFeedbackObject = {
        text: feedback,
        userId: profile.id,
      };

      await postCheckinFeedbacksMutation.mutateAsync({ checkinId, feedback: newFeedbackObject });
      getUserCheckinResponse.refetch();
    }
  };

  const handleMarkAsReviewed = async (): Promise<void> => {
    if (checkinId && id) {
      const checkinReviewed = {
        isReviewed: true,
      };

      await markAsReviewedMutation.mutateAsync({ checkinId, checkinReviewed });
      getUserCheckinResponse.refetch();
    }
  };

  const checkinsHistory = useMemo(() => {
    const allUserCheckins = userCheckinsByIdForAdmin.data ?? userCheckins.data ?? [];
    return formatCheckinHistory(allUserCheckins);
  }, [userCheckins.data, userCheckinsByIdForAdmin.data]);

  const isPageLoading = userCheckinsByIdForAdmin.isLoading || userCheckins.isLoading;

  const sortedByDate = getUserCheckinResponse.data?.feedbacks.sort(
    getComparator<Feedback>('asc', 'createdAt')
  );

  if (!checkinId || isPageLoading) return <Loading fullPage />;

  return (
    <CheckinDetails
      refetchCheckin={getUserCheckinResponse.refetch}
      checkinsHistory={checkinsHistory}
      dueDate={getUserCheckinResponse.data?.dueDate}
      answeredAt={getUserCheckinResponse.data?.answeredAt}
      user={getUserCheckinResponse.data?.user}
      answers={getUserCheckinResponse.data?.answers ?? []}
      isCheckinReviewed={!!getUserCheckinResponse.data?.reviewer?.name}
      reviewerName={getUserCheckinResponse.data?.reviewer?.name ?? ''}
      isMyCheckinDetails={isMyCheckinDetails}
      checkinId={checkinId}
      feedbacks={sortedByDate}
      handlePostFeedback={handlePostFeedback}
      handleMarkAsReviewed={handleMarkAsReviewed}
    />
  );
};

export default CheckinDetailsContainer;
